import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, CheckCircle2, XCircle, Lightbulb, Target, BookOpen, PlayCircle, Video, Filter, Search, X, Archive, ArrowRightLeft, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module definitions for display
const MODULE_DEFINITIONS = [
  { key: 'practice_sessions', label: 'Practice Sessions', icon: PlayCircle, description: 'Timed practice tests' },
  { key: 'progress_tracker', label: 'Progress Tracker', icon: Target, description: 'Analytics & progress' },
  { key: 'notebook', label: 'Study Notebook', icon: BookOpen, description: 'Personal notes' },
  { key: 'exam_insights', label: 'Exam Insights', icon: Lightbulb, description: 'Trends & analytics' },
  { key: 'live_classes', label: 'Live Classes', icon: Video, description: 'Instructor-led Zoom sessions (Tier 2)' }
];

const DEFAULT_MODULE_ACCESS = {
  practice_sessions: true,
  progress_tracker: true,
  notebook: true,
  exam_insights: true,
  live_classes: false
};

// Grade options for filtering
const GRADE_OPTIONS = [
  { value: 'all', label: 'Show All' },  // No filter - shows all plans
  { value: 'All Grades', label: 'Universal (All Grades)' },  // Filter for plans with grade = null/All Grades
  { value: 'MEO Class 1', label: 'MEO Class 1' },
  { value: 'MEO Class 2', label: 'MEO Class 2' },
  { value: 'MEO Class 4', label: 'MEO Class 4' },
  { value: 'ETO', label: 'ETO' },
  { value: 'Master', label: 'Master' },
  { value: 'Chief Mate', label: 'Chief Mate' },
  { value: '2MFG', label: '2MFG' }
];

// Duration options
const DURATION_OPTIONS = [
  { value: 'all', label: 'All Durations' },
  { value: '30', label: '1 Month (30 days)' },
  { value: '90', label: '3 Months (90 days)' },
  { value: '180', label: '6 Months (180 days)' },
  { value: '270', label: '9 Months (270 days)' },
  { value: '365', label: '1 Year (365 days)' }
];

// Status filter — default hides archived plans from the daily view.
// "all" surfaces both active + archived rows in a single grid.
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active only' },
  { value: 'archived', label: 'Archived only' },
  { value: 'all', label: 'All (Active + Archived)' },
];

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // Filter states
  const [gradeFilter, setGradeFilter] = useState('all');
  const [durationFilter, setDurationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');

  // Delete/archive/migrate modal state.
  //   deleteStep: 'idle' | 'confirm-empty' | 'choose' | 'migrate' | 'submitting'
  //   subscriberInfo: { subscriber_count, sample_emails, plan_name }
  //   migrationTargets: [{ id, name, price, duration_days, grade }, ...]
  const [deletePlan, setDeletePlan] = useState(null);
  const [deleteStep, setDeleteStep] = useState('idle');
  const [subscriberInfo, setSubscriberInfo] = useState(null);
  const [migrationTargets, setMigrationTargets] = useState([]);
  const [migrationTargetId, setMigrationTargetId] = useState('');
  const [migrationMode, setMigrationMode] = useState('preserve'); // 'preserve' | 'prorate'
  const [proratePreview, setProratePreview] = useState(null); // { migrated_user_count, skipped_expired_count, preview: [...] }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_days: 30,
    price: 0,
    grade: '',  // Empty by default, must be selected
    features: '',
    is_best_value: false,
    module_access: { ...DEFAULT_MODULE_ACCESS }
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/plans`);
      setPlans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort plans
  const filteredPlans = useMemo(() => {
    let result = [...plans];

    // Filter by archive status (default: hide archived from the grid).
    if (statusFilter === 'active') {
      result = result.filter((plan) => !plan.is_archived);
    } else if (statusFilter === 'archived') {
      result = result.filter((plan) => plan.is_archived === true);
    }
    // 'all' → no status filter applied

    // Filter by grade - strict matching (no longer includes "All Grades" when specific grade selected)
    if (gradeFilter !== 'all') {
      if (gradeFilter === 'All Grades') {
        // Show only plans that are explicitly "All Grades" (null or empty grade)
        result = result.filter(plan => !plan.grade || plan.grade === 'All Grades');
      } else {
        // Show only plans for the exact grade selected
        result = result.filter(plan => plan.grade === gradeFilter);
      }
    }
    
    // Filter by duration
    if (durationFilter !== 'all') {
      result = result.filter(plan => plan.duration_days === parseInt(durationFilter));
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(plan => 
        plan.name.toLowerCase().includes(query) ||
        plan.description?.toLowerCase().includes(query)
      );
    }
    
    // Sort by grade, then by duration, then by price
    result.sort((a, b) => {
      // First sort by grade
      const gradeA = a.grade || 'zzz'; // null grades go last
      const gradeB = b.grade || 'zzz';
      if (gradeA !== gradeB) return gradeA.localeCompare(gradeB);
      
      // Then by duration
      if (a.duration_days !== b.duration_days) return a.duration_days - b.duration_days;
      
      // Then by price
      return a.price - b.price;
    });
    
    return result;
  }, [plans, gradeFilter, durationFilter, statusFilter, searchQuery]);

  const handleCreate = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      duration_days: 30,
      price: 0,
      grade: '',  // Empty by default, must be selected
      features: '',
      is_best_value: false,
      module_access: { ...DEFAULT_MODULE_ACCESS }
    });
    setShowDialog(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      duration_days: plan.duration_days,
      price: plan.price,
      grade: plan.grade || 'all',
      features: plan.features ? plan.features.join('\n') : '',
      is_best_value: plan.is_best_value || false,
      module_access: { ...DEFAULT_MODULE_ACCESS, ...(plan.module_access || {}) }
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.description || !formData.price) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (!formData.grade) {
      toast.error('Please select a grade for this plan');
      return;
    }

    try {
      const data = {
        name: formData.name,
        description: formData.description,
        duration_days: parseInt(formData.duration_days),
        price: parseInt(formData.price),
        grade: formData.grade,  // Now mandatory
        features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : [],
        is_best_value: formData.is_best_value,
        module_access: formData.module_access
      };

      if (editingPlan) {
        await axios.put(`${API}/admin/plans/${editingPlan.id}`, data);
        toast.success('Plan updated successfully');
      } else {
        await axios.post(`${API}/admin/plans`, data);
        toast.success('Plan created successfully');
      }

      setShowDialog(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    }
  };

  const handleModuleToggle = (moduleKey) => {
    setFormData(prev => ({
      ...prev,
      module_access: {
        ...prev.module_access,
        [moduleKey]: !prev.module_access[moduleKey]
      }
    }));
  };

  const getEnabledModuleCount = (moduleAccess) => {
    if (!moduleAccess) return MODULE_DEFINITIONS.length - 1; // legacy default has all except live_classes
    return MODULE_DEFINITIONS.filter(mod => moduleAccess[mod.key] === true).length;
  };

  const handleDelete = async (planId) => {
    // Open the in-house delete modal. Fetch subscriber count first so we
    // know whether to show a simple "confirm hard delete" screen or the
    // Archive / Migrate chooser.
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    setDeletePlan(plan);
    setSubscriberInfo(null);
    setMigrationTargets([]);
    setMigrationTargetId('');
    setDeleteStep('loading');
    try {
      const r = await axios.get(`${API}/admin/plans/${planId}/subscribers`);
      setSubscriberInfo(r.data);
      setDeleteStep(r.data.subscriber_count === 0 ? 'confirm-empty' : 'choose');
    } catch (_e) {
      // Fall back to a simple confirm — API refuses when subscribers exist,
      // so it's safe to try the hard delete path.
      setSubscriberInfo({ subscriber_count: 0, sample_emails: [], plan_name: plan.name });
      setDeleteStep('confirm-empty');
    }
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeletePlan(null);
    setDeleteStep('idle');
    setSubscriberInfo(null);
    setMigrationTargets([]);
    setMigrationTargetId('');
    setMigrationMode('preserve');
    setProratePreview(null);
  };

  const submitHardDelete = async () => {
    if (!deletePlan) return;
    setDeleteSubmitting(true);
    try {
      await axios.delete(`${API}/admin/plans/${deletePlan.id}`);
      toast.success('Plan deleted');
      fetchPlans();
      closeDeleteModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plan');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const submitArchive = async () => {
    if (!deletePlan) return;
    setDeleteSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/plans/${deletePlan.id}/archive`);
      toast.success(r.data.message || 'Plan archived');
      fetchPlans();
      closeDeleteModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Archive failed');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const openMigrateStep = async () => {
    // The list of suggested targets comes back inside the 409 detail from
    // the DELETE endpoint — probe it here without actually deleting.
    if (!deletePlan) return;
    setDeleteSubmitting(true);
    try {
      await axios.delete(`${API}/admin/plans/${deletePlan.id}`);
      // Unreachable — we expected 409.
      toast.info('Plan already empty, deleted instead');
      fetchPlans();
      closeDeleteModal();
    } catch (err) {
      const targets = err.response?.data?.detail?.suggested_migration_targets || [];
      if (!targets.length) {
        toast.error('No other active plans available. Create one first.');
        setDeleteSubmitting(false);
        return;
      }
      setMigrationTargets(targets);
      setMigrationTargetId(targets[0].id);
      setDeleteStep('migrate');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const submitMigrate = async () => {
    if (!deletePlan || !migrationTargetId) return;
    setDeleteSubmitting(true);
    try {
      const endpoint = migrationMode === 'prorate' ? 'migrate-prorate' : 'migrate';
      const r = await axios.post(
        `${API}/admin/plans/${deletePlan.id}/${endpoint}`,
        null,
        { params: { target_plan_id: migrationTargetId } },
      );
      const target = migrationTargets.find((t) => t.id === migrationTargetId);
      const suffix = migrationMode === 'prorate'
        ? ` (prorated; ${r.data.skipped_expired_count || 0} expired skipped)`
        : ' (expiry preserved)';
      toast.success(`Migrated ${r.data.migrated_user_count} user(s) to ${target?.name || 'target plan'}${suffix}; source archived.`);
      fetchPlans();
      closeDeleteModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // Fetch a dry-run preview of the prorated migration so admins see the
  // per-user before/after math before committing.
  const fetchProratePreview = async (targetId) => {
    if (!deletePlan || !targetId) return;
    setPreviewLoading(true);
    setProratePreview(null);
    try {
      const r = await axios.post(
        `${API}/admin/plans/${deletePlan.id}/migrate-prorate`,
        null,
        { params: { target_plan_id: targetId, dry_run: true } },
      );
      setProratePreview(r.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUnarchive = async (planId) => {
    try {
      await axios.post(`${API}/admin/plans/${planId}/unarchive`);
      toast.success('Plan restored');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    }
  };

  const clearFilters = () => {
    setGradeFilter('all');
    setDurationFilter('all');
    setStatusFilter('active');
    setSearchQuery('');
  };

  const hasActiveFilters = gradeFilter !== 'all' || durationFilter !== 'all' || statusFilter !== 'active' || searchQuery.trim() !== '';

  const formatDuration = (days) => {
    if (days === 30) return '1 Month';
    if (days === 90) return '3 Months';
    if (days === 180) return '6 Months';
    if (days === 270) return '9 Months';
    if (days === 365) return '1 Year';
    return `${days} days`;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Pricing Plans</CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Manage subscription plans • {filteredPlans.length} of {plans.length} plans shown
              </p>
            </div>
            <Button onClick={handleCreate} data-testid="create-plan-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Plan
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* Filters Section */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-blue-600 h-6">
                  <X className="w-3 h-3 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search plans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="plan-search-input"
                />
              </div>

              {/* Status Filter (Active / Archived / All) */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`status-filter-opt-${opt.value}`}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Grade Filter */}
              <Select value={gradeFilter} onValueChange={setGradeFilter}>
                <SelectTrigger data-testid="grade-filter">
                  <SelectValue placeholder="Filter by Grade" />
                </SelectTrigger>
                <SelectContent>
                  {GRADE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Duration Filter */}
              <Select value={durationFilter} onValueChange={setDurationFilter}>
                <SelectTrigger data-testid="duration-filter">
                  <SelectValue placeholder="Filter by Duration" />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Plans Table */}
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No pricing plans created yet</p>
              <Button onClick={handleCreate} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Plan
              </Button>
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-2">No plans match your filters</p>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Grade</TableHead>
                    <TableHead className="font-semibold">Plan Name</TableHead>
                    <TableHead className="font-semibold">Duration</TableHead>
                    <TableHead className="font-semibold">Price</TableHead>
                    <TableHead className="font-semibold">Modules</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPlans.map((plan) => (
                    <TableRow key={plan.id} className="hover:bg-gray-50">
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {plan.grade || 'All Grades'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{plan.name}</span>
                          {plan.is_best_value && (
                            <Badge className="bg-yellow-500 text-white text-xs">
                              Best Value
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">
                          {plan.description}
                        </p>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{formatDuration(plan.duration_days)}</span>
                        <span className="text-xs text-gray-400 block">{plan.duration_days} days</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-blue-600">₹{plan.price}</span>
                        <span className="text-xs text-gray-400 block">
                          ₹{(plan.price / plan.duration_days).toFixed(1)}/day
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${getEnabledModuleCount(plan.module_access) === MODULE_DEFINITIONS.length ? 'bg-green-50 border-green-300 text-green-700' : 'bg-amber-50 border-amber-300 text-amber-700'}`}
                          >
                            {getEnabledModuleCount(plan.module_access)}/{MODULE_DEFINITIONS.length}
                          </Badge>
                        </div>
                        <div className="flex gap-0.5 mt-1">
                          {MODULE_DEFINITIONS.map(mod => {
                            const isEnabled = plan.module_access?.[mod.key] === true;
                            return (
                              <div 
                                key={mod.key} 
                                className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                                title={`${mod.label}: ${isEnabled ? 'Enabled' : 'Disabled'}`}
                              />
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        {plan.is_archived ? (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200" data-testid={`plan-archived-badge-${plan.id}`}>
                            Archived
                          </Badge>
                        ) : plan.is_active !== false ? (
                          <Badge className="bg-green-600 text-white">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="w-3 h-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {plan.is_archived ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnarchive(plan.id)}
                              className="h-8 px-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                              data-testid={`plan-restore-btn-${plan.id}`}
                              title="Restore this archived plan"
                            >
                              Restore
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(plan)}
                              className="h-8 px-2"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(plan.id)}
                            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`plan-delete-btn-${plan.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Delete / Archive / Migrate Modal — replaces native window.confirm/prompt */}
      <Dialog
        open={deletePlan !== null}
        onOpenChange={(open) => { if (!open) closeDeleteModal(); }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="plan-delete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {subscriberInfo?.subscriber_count > 0 ? (
                <>
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Plan has active subscribers
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5 text-red-500" />
                  Delete plan
                </>
              )}
            </DialogTitle>
            {deletePlan && (
              <DialogDescription>
                <span className="font-medium">{deletePlan.name}</span>
                {deletePlan.grade ? ` • ${deletePlan.grade}` : ''}
                {' • '}₹{deletePlan.price} / {deletePlan.duration_days}d
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Loading state */}
          {deleteStep === 'loading' && (
            <div className="py-8 text-center text-sm text-gray-500">Checking subscribers…</div>
          )}

          {/* 0 subscribers → simple confirm */}
          {deleteStep === 'confirm-empty' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                No users are on this plan. Deleting is safe and permanent.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteModal} disabled={deleteSubmitting} data-testid="plan-delete-cancel">
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={submitHardDelete}
                  disabled={deleteSubmitting}
                  data-testid="plan-delete-confirm"
                >
                  {deleteSubmitting ? 'Deleting…' : 'Delete plan'}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* > 0 subscribers → Archive / Migrate / Cancel chooser */}
          {deleteStep === 'choose' && subscriberInfo && (
            <div className="space-y-4">
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                <div className="font-medium mb-1">
                  {subscriberInfo.subscriber_count} active subscriber{subscriberInfo.subscriber_count === 1 ? '' : 's'}
                </div>
                {subscriberInfo.sample_emails?.length > 0 && (
                  <div className="text-xs text-amber-800">
                    e.g. {subscriberInfo.sample_emails.slice(0, 3).join(', ')}
                    {subscriberInfo.subscriber_count > 3 ? ` and ${subscriberInfo.subscriber_count - 3} more` : ''}
                  </div>
                )}
                <div className="text-xs mt-2 text-amber-800">
                  You can&apos;t hard-delete a plan while users are on it. Choose one:
                </div>
              </div>

              <button
                type="button"
                onClick={submitArchive}
                disabled={deleteSubmitting}
                className="w-full text-left border rounded-md p-3 hover:bg-emerald-50 hover:border-emerald-300 transition disabled:opacity-50"
                data-testid="plan-delete-archive-choice"
              >
                <div className="flex items-start gap-3">
                  <Archive className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">Archive plan</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      Hide from new shoppers. Existing users keep working until their subscription expires. Reversible from the Restore button.
                    </div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={openMigrateStep}
                disabled={deleteSubmitting}
                className="w-full text-left border rounded-md p-3 hover:bg-blue-50 hover:border-blue-300 transition disabled:opacity-50"
                data-testid="plan-delete-migrate-choice"
              >
                <div className="flex items-start gap-3">
                  <ArrowRightLeft className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">Migrate subscribers &amp; archive</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      Move all {subscriberInfo.subscriber_count} user{subscriberInfo.subscriber_count === 1 ? '' : 's'} to another active plan, then archive this one. Each user&apos;s remaining expiry is preserved.
                    </div>
                  </div>
                </div>
              </button>

              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteModal} disabled={deleteSubmitting}>
                  Cancel
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Migration target picker */}
          {deleteStep === 'migrate' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                Choose the plan to migrate {subscriberInfo?.subscriber_count} user{subscriberInfo?.subscriber_count === 1 ? '' : 's'} into. Suggested targets are ordered by closest price.
              </p>
              <Select
                value={migrationTargetId}
                onValueChange={(v) => {
                  setMigrationTargetId(v);
                  setProratePreview(null);
                  if (migrationMode === 'prorate' && v) fetchProratePreview(v);
                }}
              >
                <SelectTrigger data-testid="plan-migrate-target-select">
                  <SelectValue placeholder="Select target plan" />
                </SelectTrigger>
                <SelectContent>
                  {migrationTargets.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} — ₹{t.price} / {t.duration_days}d{t.grade ? ` • ${t.grade}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Expiry mode — Preserve vs Prorate */}
              <div className="border rounded-md p-3 space-y-2" data-testid="plan-migrate-mode-group">
                <div className="text-xs font-medium text-gray-700 mb-1">How should each user&apos;s expiry be handled?</div>
                <label className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${migrationMode === 'preserve' ? 'border-blue-400 bg-blue-50' : 'border-transparent hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="migrate-mode"
                    value="preserve"
                    checked={migrationMode === 'preserve'}
                    onChange={() => { setMigrationMode('preserve'); setProratePreview(null); }}
                    className="mt-0.5"
                    data-testid="plan-migrate-mode-preserve"
                  />
                  <div>
                    <div className="text-sm font-medium">Preserve remaining days</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      Each user keeps the exact expiry date they have today. Best for renames or splits where the target plan replaces the source at the same rate.
                    </div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${migrationMode === 'prorate' ? 'border-emerald-400 bg-emerald-50' : 'border-transparent hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="migrate-mode"
                    value="prorate"
                    checked={migrationMode === 'prorate'}
                    onChange={() => {
                      setMigrationMode('prorate');
                      if (migrationTargetId) fetchProratePreview(migrationTargetId);
                    }}
                    className="mt-0.5"
                    data-testid="plan-migrate-mode-prorate"
                  />
                  <div>
                    <div className="text-sm font-medium">Prorate to target rate</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      Convert each user&apos;s remaining ₹value into days at the target plan&apos;s per-day rate. Fair when the two plans are priced differently. Already-expired users are skipped.
                    </div>
                  </div>
                </label>
              </div>

              {/* Prorate preview */}
              {migrationMode === 'prorate' && migrationTargetId && (
                <div className="border rounded-md p-3 bg-slate-50" data-testid="plan-migrate-prorate-preview">
                  {previewLoading ? (
                    <div className="text-xs text-slate-500">Computing preview…</div>
                  ) : proratePreview ? (
                    <>
                      <div className="text-xs text-slate-700 mb-2">
                        <span className="font-medium">{proratePreview.migrated_user_count}</span> user{proratePreview.migrated_user_count === 1 ? '' : 's'} will be prorated.
                        {proratePreview.skipped_expired_count > 0 && (
                          <span className="text-amber-700"> {proratePreview.skipped_expired_count} already-expired user{proratePreview.skipped_expired_count === 1 ? '' : 's'} will be skipped.</span>
                        )}
                      </div>
                      {proratePreview.preview?.length > 0 && (
                        <div className="max-h-40 overflow-y-auto border rounded bg-white">
                          <table className="w-full text-[11px]">
                            <thead className="bg-slate-100 sticky top-0">
                              <tr className="text-left">
                                <th className="p-1.5">User</th>
                                <th className="p-1.5 text-right">Days left</th>
                                <th className="p-1.5 text-right">₹ value</th>
                                <th className="p-1.5 text-right">→ New days</th>
                              </tr>
                            </thead>
                            <tbody>
                              {proratePreview.preview.slice(0, 20).map((row) => (
                                <tr key={row.user_id} className="border-t">
                                  <td className="p-1.5 truncate max-w-[160px]">{row.user_name || row.user_email}</td>
                                  <td className="p-1.5 text-right font-mono">{row.remaining_days}</td>
                                  <td className="p-1.5 text-right font-mono">₹{row.remaining_value}</td>
                                  <td className="p-1.5 text-right font-mono text-emerald-700">{row.new_days}d</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {proratePreview.preview.length > 20 && (
                            <div className="p-1.5 text-[10px] text-slate-400 text-center">
                              …and {proratePreview.preview.length - 20} more
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-slate-500">Select a target to see the preview.</div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteStep('choose')} disabled={deleteSubmitting}>
                  Back
                </Button>
                <Button
                  className={migrationMode === 'prorate' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                  onClick={submitMigrate}
                  disabled={deleteSubmitting || !migrationTargetId || previewLoading}
                  data-testid="plan-migrate-confirm"
                >
                  {deleteSubmitting
                    ? 'Migrating…'
                    : migrationMode === 'prorate'
                      ? 'Prorate, migrate & archive'
                      : 'Migrate & archive'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Edit Plan' : 'Create New Plan'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Plan Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Monthly Premium"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the plan"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duration *</Label>
                <Select
                  value={formData.duration_days.toString()}
                  onValueChange={(value) => setFormData({...formData, duration_days: parseInt(value)})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">1 Month (30 days)</SelectItem>
                    <SelectItem value="90">3 Months (90 days)</SelectItem>
                    <SelectItem value="180">6 Months (180 days)</SelectItem>
                    <SelectItem value="270">9 Months (270 days)</SelectItem>
                    <SelectItem value="365">1 Year (365 days)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Standard durations for pro-rata calculations</p>
              </div>

              <div>
                <Label>Price (₹) *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  placeholder="299"
                />
              </div>
            </div>

            <div>
              <Label>Grade *</Label>
              <Select
                value={formData.grade}
                onValueChange={(value) => setFormData({...formData, grade: value})}
              >
                <SelectTrigger className={!formData.grade ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Select a grade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Grades">All Grades</SelectItem>
                  <SelectItem value="MEO Class 1">MEO Class 1</SelectItem>
                  <SelectItem value="MEO Class 2">MEO Class 2</SelectItem>
                  <SelectItem value="MEO Class 4">MEO Class 4</SelectItem>
                  <SelectItem value="ETO">ETO</SelectItem>
                  <SelectItem value="Master">Master</SelectItem>
                  <SelectItem value="Chief Mate">Chief Mate</SelectItem>
                  <SelectItem value="2MFG">2MFG</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Select a specific grade or &ldquo;All Grades&rdquo; for universal plans</p>
            </div>

            <div>
              <Label>Features (Optional)</Label>
              <Textarea
                value={formData.features}
                onChange={(e) => setFormData({...formData, features: e.target.value})}
                placeholder="Enter each feature on a new line&#10;Access to all questions&#10;Priority support"
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">One feature per line</p>
            </div>

            {/* Best Value Toggle */}
            <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <input
                type="checkbox"
                id="is_best_value"
                checked={formData.is_best_value}
                onChange={(e) => setFormData({...formData, is_best_value: e.target.checked})}
                className="w-5 h-5 rounded border-yellow-400 text-yellow-500 focus:ring-yellow-500"
              />
              <div>
                <Label htmlFor="is_best_value" className="text-yellow-800 font-medium cursor-pointer">
                  Mark as &quot;Best Value&quot;
                </Label>
                <p className="text-xs text-yellow-700">This plan will be highlighted with a &quot;Best Value&quot; badge</p>
              </div>
            </div>

            {/* Module Access Configuration */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-sm font-medium text-blue-800 mb-3 block">
                Module Access
              </Label>
              <p className="text-xs text-blue-600 mb-4">
                Select which premium modules users with this plan can access
              </p>
              <div className="space-y-3">
                {MODULE_DEFINITIONS.map(mod => {
                  const Icon = mod.icon;
                  return (
                    <div 
                      key={mod.key}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-sm">{mod.label}</p>
                          <p className="text-xs text-gray-500">{mod.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.module_access[mod.key] === true}
                        onCheckedChange={() => handleModuleToggle(mod.key)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1"
            >
              {editingPlan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PlanManagement;
